import "./adapter";
import { DOMVideo } from "../dom/DOMVideo";

export class WebRTCClient {
  localVideoUtil: DOMVideo;
  remoteVideoUtil: DOMVideo;
  localPeerConnection: RTCPeerConnection | null = null;
  remotePeerConnection: RTCPeerConnection | null = null;

  constructor(localVideoEl: HTMLVideoElement, remoteVideoEl: HTMLVideoElement) {
    this.localVideoUtil = new DOMVideo(localVideoEl);
    this.remoteVideoUtil = new DOMVideo(remoteVideoEl);
  }

  start() {
    this.getLocalMediaStream().then((mediaStream) => {
      this.call(mediaStream);
    });
  }

  end() {
    this.localPeerConnection?.close();
    this.remotePeerConnection?.close();
    this.localPeerConnection = null;
    this.remotePeerConnection = null;
    this.trace("Ending call.");
  }

  getLocalMediaStream() {
    return new Promise<MediaStream>((resolve, reject) => {
      navigator.mediaDevices
        .getUserMedia({
          audio: true,
          video: true
        })
        .then((mediaStream) => {
          this.localVideoUtil.setSrcObject(mediaStream);
          resolve(mediaStream);
        })
        .catch((error) => {
          this.trace(`navigator.getUserMedia error: ${error}.`);
          reject(error);
        });
    });
  }

  gotRemoteMediaStream(event: RTCTrackEvent) {
    const mediaStream = event.streams[0];
    this.remoteVideoUtil.setSrcObject(mediaStream);
    this.trace("Remote peer connection received remote stream.");
  }

  call(localStream: MediaStream) {
    const videoTracks = localStream.getVideoTracks();
    const audioTracks = localStream.getAudioTracks();
    if (videoTracks.length > 0) {
      this.trace(`Using video device: ${videoTracks[0].label}.`);
    }
    if (audioTracks.length > 0) {
      this.trace(`Using audio device: ${audioTracks[0].label}.`);
    }

    // Allows for RTC server configuration.
    const servers = undefined;

    // Create peer connections and add behavior.
    const localPeerConnection = new RTCPeerConnection(servers);
    this.localPeerConnection = localPeerConnection;
    this.trace("Created local peer connection object localPeerConnection.");
    localPeerConnection.addEventListener("icecandidate", this.handleConnection);
    localPeerConnection.addEventListener(
      "iceconnectionstatechange",
      this.handleConnectionChange
    );

    const remotePeerConnection = new RTCPeerConnection(servers);
    this.remotePeerConnection = remotePeerConnection;
    this.trace("Created remote peer connection object remotePeerConnection.");
    remotePeerConnection.addEventListener(
      "icecandidate",
      this.handleConnection
    );
    remotePeerConnection.addEventListener(
      "iceconnectionstatechange",
      this.handleConnectionChange
    );
    remotePeerConnection.addEventListener("track", this.gotRemoteMediaStream);

    this.trace("Add local localStream to localPeerConnection.");
    localPeerConnection.addTrack(videoTracks[0], localStream);
    localPeerConnection.addTrack(audioTracks[0], localStream);

    this.trace("localPeerConnection createOffer start.");
    localPeerConnection
      .createOffer({
        offerToReceiveVideo: true
      })
      .then((description) => this.createdOffer(description))
      .catch((err) => this.setSessionDescriptionError(err));
  }

  handleConnection(event: RTCPeerConnectionIceEvent) {
    const peerConnection = event.target as RTCPeerConnection | null;
    const iceCandidate = event.candidate;

    if (peerConnection && iceCandidate) {
      const newIceCandidate = new RTCIceCandidate(iceCandidate);
      const otherPeer = this.getOtherPeer(peerConnection);

      if (otherPeer) {
        otherPeer
          .addIceCandidate(newIceCandidate)
          .then(() => {
            this.handleConnectionSuccess(peerConnection);
          })
          .catch((error: any) => {
            this.handleConnectionFailure(peerConnection, error);
          });
        this.trace(
          `${this.getPeerName(peerConnection)} ICE candidate:\n` +
            `${event.candidate.candidate}.`
        );
      }
    }
  }

  handleConnectionSuccess(peerConnection: RTCPeerConnection) {
    this.trace(`${this.getPeerName(peerConnection)} addIceCandidate success.`);
  }

  handleConnectionFailure(peerConnection: RTCPeerConnection, error: any) {
    this.trace(
      `${this.getPeerName(peerConnection)} failed to add ICE Candidate:\n` +
        `${error.toString()}.`
    );
  }

  handleConnectionChange(event: Event) {
    const peerConnection = event.target as RTCPeerConnection | null;
    console.log("ICE state change event: ", event);
    if (peerConnection) {
      this.trace(
        `${this.getPeerName(peerConnection)} ICE state: ` +
          `${peerConnection.iceConnectionState}.`
      );
    }
  }

  createdOffer(description: RTCSessionDescriptionInit) {
    this.trace(`Offer from localPeerConnection:\n${description.sdp}`);

    if (this.localPeerConnection) {
      this.trace("localPeerConnection setLocalDescription start.");
      this.localPeerConnection
        .setLocalDescription(description)
        .then(() => {
          this.setLocalDescriptionSuccess(this.localPeerConnection!);
        })
        .catch((err) => this.setSessionDescriptionError(err));
    }

    if (this.remotePeerConnection) {
      this.trace("remotePeerConnection setRemoteDescription start.");
      this.remotePeerConnection
        .setRemoteDescription(description)
        .then(() => {
          this.setRemoteDescriptionSuccess(this.remotePeerConnection!);
        })
        .catch((err) => this.setSessionDescriptionError(err));

      this.trace("remotePeerConnection createAnswer start.");
      this.remotePeerConnection
        .createAnswer()
        .then((description) => this.createdAnswer(description))
        .catch((err) => this.setSessionDescriptionError(err));
    }
  }

  createdAnswer(description: RTCSessionDescriptionInit) {
    this.trace(`Answer from remotePeerConnection:\n${description.sdp}.`);

    if (this.remotePeerConnection) {
      this.trace("remotePeerConnection setLocalDescription start.");
      this.remotePeerConnection
        .setLocalDescription(description)
        .then(() => {
          this.setLocalDescriptionSuccess(this.remotePeerConnection!);
        })
        .catch((err) => this.setSessionDescriptionError(err));
    }

    if (this.localPeerConnection) {
      this.trace("localPeerConnection setRemoteDescription start.");
      this.localPeerConnection
        .setRemoteDescription(description)
        .then(() => {
          this.setRemoteDescriptionSuccess(this.localPeerConnection!);
        })
        .catch((err) => this.setSessionDescriptionError(err));
    }
  }

  setSessionDescriptionError(error: any) {
    this.trace(`Failed to create session description: ${error.toString()}.`);
  }

  setDescriptionSuccess(
    peerConnection: RTCPeerConnection,
    functionName: string
  ) {
    const peerName = this.getPeerName(peerConnection);
    this.trace(`${peerName} ${functionName} complete.`);
  }

  setLocalDescriptionSuccess(peerConnection: RTCPeerConnection) {
    this.setDescriptionSuccess(peerConnection, "setLocalDescription");
  }

  setRemoteDescriptionSuccess(peerConnection: RTCPeerConnection) {
    this.setDescriptionSuccess(peerConnection, "setRemoteDescription");
  }

  trace(text: string) {
    text = text.trim();
    const now = (window.performance.now() / 1000).toFixed(3);
    console.log(now, text);
    const div = document.createElement("div");
    div.innerHTML = `<div>${now}, ${text}</div>`;
    document.getElementById("trace")?.appendChild(div);
  }

  getOtherPeer(peerConnection: RTCPeerConnection) {
    return peerConnection === this.localPeerConnection
      ? this.remotePeerConnection
      : this.localPeerConnection;
  }

  getPeerName(peerConnection: RTCPeerConnection) {
    return peerConnection === this.localPeerConnection
      ? "localPeerConnection"
      : "remotePeerConnection";
  }
}

export default WebRTCClient;
